import type { LatLngBounds } from "leaflet";

// Bounding box helpers shared by the map widget and the save validators. Kept out
// of the widget so the validators do not pull in Leaflet.
//
// The stored value is a JSON string "[ne_lat, ne_lng, sw_lat, sw_lng]" (the order
// the iNaturalist connector parses). While the user's typed coordinates are not a
// valid box, the field holds what they typed instead, in the same order, so it
// survives the form re-rendering and the save validators can reject it.
export type Box = { west: number; south: number; east: number; north: number };
export type BoxField = keyof Box;
export type BoxInputs = Record<BoxField, string>;

export const BBOX_WIDGET = "bbox";
export const INVALID_BOX_MESSAGE =
    "Enter a valid area: latitudes between -90 and 90, longitudes between -180 and 180, north greater than south and east greater than west.";

export const round = (n: number) => Math.round(n * 1e6) / 1e6;

const parseArray = (value: unknown): unknown[] | null => {
    if (value === null || value === undefined || value === "") return null;
    try {
        const raw = typeof value === "string" ? JSON.parse(value) : value;
        return Array.isArray(raw) && raw.length >= 4 ? raw.slice(0, 4) : null;
    } catch {
        return null;
    }
};

// The four numbers in the value, or null when any is missing or not a number.
// A parsed box can still be out of range; check it with isValidBox.
export const parseBox = (value: unknown): Box | null => {
    const raw = parseArray(value);
    if (!raw || raw.some((n) => n === null || String(n).trim() === "")) return null;
    const [north, east, south, west] = raw.map(Number);
    if ([north, east, south, west].some((n) => Number.isNaN(n))) return null;
    return { west, south, east, north };
};

// What the four inputs should show for a stored value: its text, exactly as saved
// or as typed.
export const parseInputs = (value: unknown): BoxInputs => {
    const raw = parseArray(value);
    const text = (i: number) => (raw && raw[i] !== null && raw[i] !== undefined ? String(raw[i]) : "");
    return { north: text(0), east: text(1), south: text(2), west: text(3) };
};

export const serializeBox = (b: Box): string =>
    JSON.stringify([b.north, b.east, b.south, b.west].map(round));

// Typed text kept as-is, in the stored order, for a box that is not valid yet.
export const serializeInputs = (inputs: BoxInputs): string =>
    JSON.stringify([inputs.north, inputs.east, inputs.south, inputs.west]);

// Checked on the rounded edges, since those are what get saved: a box thinner
// than the 6-decimal precision would otherwise be stored as zero area.
export const isValidBox = (b: Box): boolean => {
    const r = { west: round(b.west), south: round(b.south), east: round(b.east), north: round(b.north) };
    return r.north <= 90 && r.south >= -90 && r.east <= 180 && r.west >= -180 &&
        r.north > r.south && r.east > r.west;
};

// The map can show more than one world (zoomed out, or panned across the date
// line), so its bounds are clamped to the valid range before they are saved.
export const boxFromBounds = (b: LatLngBounds): Box => ({
    west: Math.max(-180, b.getWest()),
    south: Math.max(-90, b.getSouth()),
    east: Math.min(180, b.getEast()),
    north: Math.min(90, b.getNorth()),
});

// Save-time check for every field the connector marks with ui:widget "bbox": a
// non-empty value must be a valid box. Returns { field: message }.
export const boundingBoxErrors = (
    uiSchema: any,
    data: any,
    t: (key: any, options?: any) => string = (key: any) => key,
): Record<string, string> => {
    const errors: Record<string, string> = {};
    Object.entries(uiSchema || {}).forEach(([field, options]: [string, any]) => {
        if (options?.["ui:widget"] !== BBOX_WIDGET) return;
        const value = data?.[field];
        if (value === null || value === undefined || value === "") return;
        const box = parseBox(value);
        if (!box || !isValidBox(box)) errors[field] = t(INVALID_BOX_MESSAGE);
    });
    return errors;
};
